import { BuydownType, WorkoutPlanType } from '@prisma/client';
import * as z from 'zod';

import { CompanyId, LoanId, LosId, PoolId, UserId } from '../BrandedIds';
import { DrawType } from '../loanFilter/LoanFilter';
import { StateAbbreviation } from '../validations/fields';
import { AmortizationType, LoanInvestorName, ProductType } from './Loan';
import { LoanStatus } from './LoanStatus';
import { NonPerformingReportStatus } from './NonPerformance';
import { LoanPaymentStatus } from './Snapshots';

export interface LoanReporting {
  id: LoanId;
  loanId: LoanId;
  losId: LosId;
  companyId: CompanyId;

  // Additional ids for loan
  poolId?: PoolId; // Loan pool if doing full life of loan servicing
  previousServicerId?: string; // id if the loan had previously been with another servicer
  capitalPartnerId?: string; // id of the loan investor
  fullId?: string; // Long id for loan
  otherId1?: string; // Extra id field to be used as needed
  otherId2?: string; // Extra id field to be used as needed
  otherId3?: string; // Extra id field to be used as needed
  ownerId?: string; // Id for owner of the loan if not the servicer
  ownerLoanId?: string; // Id provided by the owner for this loan

  name: string;
  primaryBorrowerEmail?: string;
  address: string;
  addressState: StateAbbreviation;
  status: LoanStatus;
  nonPerformingStatus: NonPerformingReportStatus;
  flagged: boolean;
  amortizationType: AmortizationType;
  productType: ProductType;
  productDescription?: string;
  interestRate: number;
  buydownRate?: number;
  monthlyInterestRate: number;
  loanCreatedAt: Date;
  hasInterestReserve: boolean;
  buydownType?: BuydownType;
  servicer?: string;
  transferDate?: Date;
  goodbyeEmailStatus: EmailStatus;
  mortgageeEmailStatus: EmailStatus;
  autoPaymentType: AutoPaymentType;
  autoPaymentDayOfMonth: number;
  paymentStatus: LoanReportingPaymentStatus;
  paymentFrequency: number;
  lastPaymentDate?: Date;
  nextPaymentDate?: Date;
  lastFulfilledPaymentDate?: Date;
  paidOffDate?: Date;
  oldestOverduePaymentDate?: Date;
  daysPastDue: number;
  daysPastGrace: number;
  gracePeriodDays: number;
  paystring: string;
  monthlyPaymentAmount: number;
  firstPaymentDueDate: Date;
  fundingDate: Date;
  originalPrincipalBalance: number;
  outstandingPrincipalBalance: number;
  principalPrePaymentBalance: number;
  escrowBalance: number;
  suspenseBalance: number;
  unappliedSuspenseBalance: number;
  reserveBalance: number;
  holdbackBalance: number;
  lossDraftBalance: number;
  advancePrincipalBalance: number;
  feesOutstanding: number;
  maturityDate?: Date;
  totalAmountDue: number;
  expectAnyPayment: boolean;
  firstUnfulfilledPaymentDate?: Date;
  totalAmountCollected: number;
  hasBorrowerLoggedIn: boolean;
  hasBorrowerConnectedAccount: boolean;
  totalCommunicationsSent: number;
  constructionStatus: ConstructionStatus;
  entityName?: string;
  workoutPlanType: WorkoutPlanType;
  workoutPlanTypes: WorkoutPlanType[]; // all workout plans currently or scheduled to be active
  drawsEnabled: boolean;
  remainingCredit: number;
  maxLineOfCredit: number;
  investorName?: LoanInvestorName;
  pointOfContactId?: UserId;
  delinquentInterestRate?: number;
  delinquentInterestStartDate?: Date;
  delinquentInterestEndDate?: Date;
  bankruptcyChapter: LoanReportingBankruptcyChapter;
  foreclosureStatus: LoanReportingForeclosureStatus;
  drawType?: DrawType;
}

export const EmailStatus = z.enum(['NONE', 'SCHEDULED', 'SENT']);
export type EmailStatus = z.infer<typeof EmailStatus>;
export const LoanReportingPaymentStatus = z.enum([
  ...LoanPaymentStatus.options,
  'PASTDUE_30',
  'PASTDUE_60',
  'PASTDUE_90',
]);
export type LoanReportingPaymentStatus = z.infer<typeof LoanReportingPaymentStatus>;

export const ConstructionStatus = z.enum([
  'NON_CONSTRUCTION',
  'CONSTRUCTION_ACTIVE',
  'CONSTRUCTION_TO_PERMANENT_ACTIVE',
  'CONSTRUCTION_TO_PERMANENT_PERMANENT',
]);

export type ConstructionStatus = z.infer<typeof ConstructionStatus>;

export const AutoPaymentType = z.enum(['NONE', 'BORROWER_INITIATED', 'LENDER_INITIATED']);
export type AutoPaymentType = z.infer<typeof AutoPaymentType>;

export const LoanReportingWorkoutPlanType = z.enum([
  'NONE',
  'PAYMENT_DEFERMENT',
  'PAYMENT_DEFERRAL',
  'PAYMENT_DEFERRAL_OFFERED',
  'FLEX_MODIFICATION',
  'FORBEARANCE',
  'REPAYMENT_PLAN',
]);
export type LoanReportingWorkoutPlanType = z.infer<typeof LoanReportingWorkoutPlanType>;

export const LoanReportingBankruptcyChapter = z.enum(['NONE', 'CHAPTER_7', 'CHAPTER_11', 'CHAPTER_12', 'CHAPTER_13']);
export type LoanReportingBankruptcyChapter = z.infer<typeof LoanReportingBankruptcyChapter>;

export const LoanReportingForeclosureStatus = z.enum(['NONE', 'ACTIVE', 'ON_HOLD', 'COMPLETED']);
export type LoanReportingForeclosureStatus = z.infer<typeof LoanReportingForeclosureStatus>;
